import React, { PropsWithChildren } from 'react'
import { Select } from '@/components/Select'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { CreateClientTest } from 'api/Clients/Types/CreateClientTest'
import { TestAnswerDto } from 'api/Clients/Types/TestDto'
import {
  TestQuestionDto,
  TestQuestionWidget,
  TestQuestionWidgetType,
} from 'api/Clients/Types/TestQuestionDto'
import { TestSectionsDto } from 'api/Clients/Types/TestSectionsDto'
import { Box } from 'components/Box'
import { SectionContainer } from 'components/QuestionAnswerSection'
import { Form, FormikErrors, FormikProps, getIn, useFormikContext, withFormik } from 'formik'
import { useAppSelector } from 'store'
import styles from 'styles/components/QuestionAnswerSection/questionAnswerSection.module.scss'
import { FormSubmitValues } from 'utils/formValidation'

// TODO: migrate to FormFields the bellow
const ClientTestFormUI: React.FC<FormikProps<TestFormValues> & OuterProps> = (props) => {
  const { test, isSubmitting, isValid } = props
  const context = useFormikContext<TestFormValues>()
  const allQuestions = test.sections.flatMap((section) => section.questions)

  return (
    <Form>
      <Wrapper>
        {test.sections.map((section) => (
          <SectionContainer isNewWindow key={section.id}>
            <label>{section.name}</label>
            {section.questions
              .filter(({ widget }) =>
                showConditionalQuestions(allQuestions, context.values.selectedAnswers, widget)
              )
              .map((q) => {
                const error = getIn(context.errors, q.widget.name || '')
                const touched = getIn(context.touched, q.widget.name || '')
                const showError = !!touched && !!error

                return (
                  <React.Fragment key={q.question.id}>
                    <WidgetFactory question={q} questions={allQuestions} />
                    {showError && <div className='text-xs mt-0 text-red-500'>{String(error)}</div>}
                  </React.Fragment>
                )
              })}
          </SectionContainer>
        ))}
        <Button type='submit' className='max-w-35' disabled={isSubmitting || !isValid}>
          Submit
        </Button>
      </Wrapper>
    </Form>
  )
}

const Wrapper: React.FC<PropsWithChildren> = ({ children }) => (
  <Box
    display='flex'
    flexDirection='column'
    style={{ margin: '1rem', paddingTop: '1rem', paddingBottom: '1rem' }}
  >
    {children}
  </Box>
)

interface WidgetProps {
  question: TestQuestionDto
  questions?: TestQuestionDto[]
}

export const WidgetFactory: React.FC<WidgetProps> = (props) => {
  const { question: q, questions } = props

  switch (q.widget.type) {
    case TestQuestionWidgetType.Checkbox:
      return <CheckboxWidget question={q} />
    case TestQuestionWidgetType.MultiSelect:
      return <MultiSelectWidget question={q} questions={questions} />
    case TestQuestionWidgetType.ShortText:
    case TestQuestionWidgetType.Text:
      return <InputWidget question={q} />
    case TestQuestionWidgetType.Select:
      return <SelectWidget question={q} questions={questions} />
    case TestQuestionWidgetType.Radio:
      return <RadioWidget question={q} questions={questions} />
    case TestQuestionWidgetType.Country:
      return <CountryWidget question={q} />
    default:
      return <InputWidget question={q} />
  }
}

const RadioWidget: React.FC<WidgetProps> = (props) => {
  const { question: q, questions } = props
  const { values, setValues, setFieldTouched, touched, setTouched, errors, setErrors } =
    useFormikContext<TestFormValues>()

  const fieldName = q.widget.name || q.question.id
  const selectedAnswer = values.selectedAnswers.find((id) => q.answers.some((a) => a.id === id))

  const handleChange = (answerId: string) => {
    const answerIds = q.answers.map((answer) => answer.id)
    const cleanedValues = values.selectedAnswers.filter((value) => !answerIds.includes(value))

    const nextSelectedAnswers = handleFilterOnChange(cleanedValues, answerId, q, questions)

    const filtered = excludeHiddenQuestionValues(
      questions || [],
      nextSelectedAnswers,
      values.freeAnswers,
      values.tempAnswers
    )

    setValues({
      ...values,
      selectedAnswers: filtered.selectedAnswers,
      freeAnswers: filtered.freeAnswers,
      tempAnswers: filtered.tempAnswers,
    })

    const newTouched = { ...touched, [fieldName]: true }
    const newErrors = { ...errors }

    for (const name of filtered.hiddenFieldNames) {
      delete newTouched[name as keyof TestFormValues]
      delete newErrors[name as keyof TestFormValues]
    }

    setTouched(newTouched, false)
    setErrors(newErrors)
  }

  return (
    <div>
      <span>
        {q.question.name}
        <span className='error-text'>{isQuestionMandatory(q) ? '*' : null}</span>
      </span>
      <RadioGroup
        name={fieldName}
        value={selectedAnswer}
        onValueChange={handleChange}
        onBlur={() => setFieldTouched(fieldName, true, false)}
        className='flex flex-col gap-1 mt-2 px-0'
      >
        {q.answers.map((answer) => (
          <div key={answer.id} className={styles.multiSelect}>
            <RadioGroupItem value={answer.id} />
            <span className='text-sm'>{answer.name}</span>
          </div>
        ))}
      </RadioGroup>
    </div>
  )
}

const CheckboxWidget: React.FC<WidgetProps> = (props) => {
  const { question: q } = props
  const { values, setFieldValue, setFieldTouched } = useFormikContext<TestFormValues>()
  const freeAnswer = values.freeAnswers.find((x) => x.question === q.question.id)
  const fieldName = q.widget.name || q.question.id

  const handleChange = (checked: boolean) => {
    const filteredArray = values.freeAnswers.filter((x) => x.question !== q.question.id)
    const newValue = { question: q.question.id, value: checked ? 1 : 0 }
    const newVal = [...filteredArray, newValue]
    setFieldValue('freeAnswers', newVal)
    setFieldTouched(fieldName, true, false)
  }

  return (
    <div>
      <span>
        {q.question.name} <span className='error-text'>{isQuestionMandatory(q) ? '*' : null}</span>
      </span>
      <Checkbox
        name={fieldName}
        checked={freeAnswer?.value === 1}
        onCheckedChange={(checked) => handleChange(Boolean(checked))}
        onBlur={() => setFieldTouched(fieldName, true, false)}
      />
    </div>
  )
}

const MultiSelectWidget: React.FC<WidgetProps> = (props) => {
  const { question: q, questions = [] } = props
  const { values, setFieldTouched, setValues, touched, setTouched, setErrors, errors } =
    useFormikContext<TestFormValues>()

  if (!q.widget.name) return null

  const selectedAnswers = values.selectedAnswers
  const fieldPath = q.widget.name

  const handleChange = (answerId: string) => {
    const isSelected = selectedAnswers.includes(answerId)

    let nextSelectedAnswers: string[]

    if (isSelected) {
      nextSelectedAnswers = selectedAnswers.filter((a) => a !== answerId)
    } else {
      const answerGroups = q.widget.widgetOptions?.answerGroups
      const selectedGroup = answerGroups?.find((group) => group.values.includes(answerId))

      if (selectedGroup) {
        const questionAnswerIds = new Set(q.answers.map((a) => a.id))
        const currentAnswersForQuestion = selectedAnswers.filter((id) => questionAnswerIds.has(id))
        const answersFromOtherQuestions = selectedAnswers.filter((id) => !questionAnswerIds.has(id))
        const otherGroups = answerGroups?.filter((g) => g.name !== selectedGroup.name) || []
        const otherGroupAnswerIds = new Set(otherGroups.flatMap((g) => g.values))

        const cleanedQuestionAnswers = currentAnswersForQuestion.filter(
          (id) => !otherGroupAnswerIds.has(id)
        )

        nextSelectedAnswers = [...answersFromOtherQuestions, ...cleanedQuestionAnswers, answerId]
      } else {
        nextSelectedAnswers = [...selectedAnswers, answerId]
      }
    }

    const filtered = excludeHiddenQuestionValues(
      questions,
      nextSelectedAnswers,
      values.freeAnswers,
      values.tempAnswers
    )

    setValues({
      ...values,
      selectedAnswers: filtered.selectedAnswers,
      freeAnswers: filtered.freeAnswers,
      tempAnswers: filtered.tempAnswers,
    })

    const newTouched = { ...touched, [fieldPath]: true }
    const newErrors = { ...errors }
    for (const name of filtered.hiddenFieldNames) {
      delete newTouched[name as keyof TestFormValues]
      delete newErrors[name as keyof TestFormValues]
    }
    setTouched(newTouched, false)
    setErrors(newErrors)
  }

  return (
    <div>
      <span>
        {q.question.name} <span className='error-text'>{q.isMandatory ? '*' : null}</span>
      </span>

      {q.answers.map((answer) => (
        <div className='flex items-center gap-2 px-2 py-2 rounded hover:bg-gray-50' key={answer.id}>
          <Checkbox
            name={fieldPath}
            checked={selectedAnswers.includes(answer.id)}
            onCheckedChange={() => handleChange(answer.id)}
            onBlur={() => setFieldTouched(fieldPath, true, false)}
            className='shrink-0'
          />
          <span className='text-sm'>{answer.name}</span>
        </div>
      ))}
    </div>
  )
}

const InputWidget: React.FC<WidgetProps> = (props) => {
  const { question: q } = props
  const { values, setFieldValue, setFieldTouched } = useFormikContext<TestFormValues>()

  const freeAnswer = values.freeAnswers.find((x) => x.question === q.question.id)
  const fieldName = q.widget.name || q.question.id

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    const filteredArray = values.freeAnswers.filter((x) => x.question !== q.question.id)

    const newValue = { question: q.question.id, value }
    const newVal = [...filteredArray, newValue]
    setFieldValue('freeAnswers', newVal)
    setFieldTouched(fieldName, true, false)
  }

  return (
    <div>
      <span>
        {q.question.name} <span className='error-text'>{isQuestionMandatory(q) ? '*' : null}</span>
      </span>
      <div className={styles.inputWrapper}>
        <Input
          name={fieldName}
          type='text'
          value={(freeAnswer && freeAnswer.value) || ''}
          onChange={handleChange}
          onBlur={() => setFieldTouched(fieldName, true, false)}
        />
      </div>
    </div>
  )
}

const excludeHiddenQuestionValues = (
  questions: TestQuestionDto[],
  nextSelectedAnswers: string[],
  freeAnswers: { question: string; value: string | number }[],
  tempAnswers: { question: string; value: string }[]
) => {
  const hiddenQuestions = questions.filter(
    (qq) => !showConditionalQuestions(questions, nextSelectedAnswers, qq.widget)
  )

  const hiddenQuestionIds = new Set(hiddenQuestions.map((qq) => qq.question.id))
  const hiddenAnswerIds = new Set(hiddenQuestions.flatMap((qq) => qq.answers.map((a) => a.id)))
  const hiddenFieldNames = hiddenQuestions
    .map((qq) => qq.widget.name)
    .filter((name): name is string => !!name)

  return {
    selectedAnswers: nextSelectedAnswers.filter((id) => !hiddenAnswerIds.has(id)),
    freeAnswers: freeAnswers.filter((x) => !hiddenQuestionIds.has(x.question)),
    tempAnswers: tempAnswers.filter((x) => !hiddenQuestionIds.has(x.question)),
    hiddenFieldNames,
  }
}

const handleFilterOnChange = (
  cleanedValues: string[],
  answerId: string,
  q: TestQuestionDto,
  questions: TestQuestionDto[] = []
) => {
  const widgetOptionsData = q?.widget?.widgetOptions?.onChange || []

  const fieldsToReset = widgetOptionsData
    .filter((x) => !x.values?.includes(answerId))
    .filter((x) => !!x.name && x.name !== q.widget.name)

  const getAnswerIdsToResetByNames = (
    fields: { name?: string; values?: string[] }[],
    allQuestions: TestQuestionDto[]
  ) =>
    fields.flatMap((field) => {
      const targetQ = allQuestions.find((qq) => qq.widget.name === field.name)
      return targetQ ? targetQ.answers.map((a) => a.id) : []
    })

  const resetAnswerIdsByNames = getAnswerIdsToResetByNames(fieldsToReset, questions)

  const nextSelectedAnswers = [...new Set(cleanedValues.concat(answerId))]

  const hiddenAnswerIds = questions
    .filter((qq) => !showConditionalQuestions(questions, nextSelectedAnswers, qq.widget))
    .flatMap((qq) => qq.answers.map((a) => a.id))

  const idsToRemove = new Set([...resetAnswerIdsByNames, ...hiddenAnswerIds])

  return nextSelectedAnswers.filter((id) => !idsToRemove.has(id))
}

const SelectWidget: React.FC<WidgetProps> = (props) => {
  const { question: q, questions } = props
  const { values, setValues, setFieldTouched, touched, setTouched, errors, setErrors } =
    useFormikContext<TestFormValues>()

  const fieldName = q.widget.name || q.question.id

  const answer = values.selectedAnswers.find(
    (selectedAnswer) =>
      selectedAnswer === q.answers.find((answer) => answer.id === selectedAnswer)?.id
  )

  const handleChange = (answerId: string) => {
    const questionAnswers = q.answers
    const selectedAnswers = values.selectedAnswers

    const answerIds = questionAnswers.map((answer) => answer.id)
    const cleanedValues = selectedAnswers.filter((value) => !answerIds.includes(value)) ?? []

    const nextSelectedAnswers =
      answerId === '' ? cleanedValues : handleFilterOnChange(cleanedValues, answerId, q, questions)

    const filtered = excludeHiddenQuestionValues(
      questions || [],
      nextSelectedAnswers,
      values.freeAnswers,
      values.tempAnswers
    )

    setValues({
      ...values,
      selectedAnswers: filtered.selectedAnswers,
      freeAnswers: filtered.freeAnswers,
      tempAnswers: filtered.tempAnswers,
    })

    const newTouched = { ...touched, [fieldName]: true }
    const newErrors = { ...errors }
    for (const name of filtered.hiddenFieldNames) {
      delete newTouched[name as keyof TestFormValues]
      delete newErrors[name as keyof TestFormValues]
    }
    setTouched(newTouched, false)
    setErrors(newErrors)
  }

  return (
    <div>
      <span>
        {q.question.name}
        <span className='error-text'>{isQuestionMandatory(q) ? '*' : null}</span>
      </span>
      <div className={styles.inputWrapper}>
        <Select
          name={fieldName}
          value={answer || ''}
          options={q.answers.map((option) => ({
            value: option.id,
            label: option.name,
          }))}
          placeholder='Not set'
          allowClear
          fullWidth
          onChange={(value) => handleChange(value == null ? '' : String(value))}
          onBlur={() => setFieldTouched(fieldName, true, false)}
        />
      </div>
    </div>
  )
}

const CountryWidget: React.FC<WidgetProps> = (props) => {
  const { question: q } = props
  const { values, setFieldValue, setFieldTouched } = useFormikContext<TestFormValues>()

  const countries = useAppSelector((state) => state.identity.countries)
  const fieldName = q.widget.name || q.question.id

  const answer = [...values.tempAnswers, ...values.freeAnswers].find(
    (x) => x.question === q.question.id
  )

  const rawValue = answer?.value == null ? '' : String(answer.value)

  const selectedCountry = countries.find(
    (country) => country.id === rawValue || country.name === rawValue
  )

  const selectValue = selectedCountry?.name || rawValue

  const handleChange = (value: string) => {
    const filteredArray = values.tempAnswers.filter((x) => x.question !== q.question.id)

    const newValue = { question: q.question.id, value }
    const newVal = [...filteredArray, newValue]
    setFieldValue('tempAnswers', newVal)
    setFieldTouched(fieldName, true, false)
  }

  return (
    <div>
      <span>
        {q.question.name}
        <span className='error-text'>{isQuestionMandatory(q) ? '*' : null}</span>
      </span>

      <div className={styles.inputWrapper}>
        <Select
          name={fieldName}
          value={selectValue}
          options={countries.map((option) => ({
            value: option.name,
            label: option.name,
          }))}
          placeholder='Not set'
          allowClear
          fullWidth
          onChange={(value) => handleChange(value == null ? '' : String(value))}
          onBlur={() => setFieldTouched(fieldName, true, false)}
        />
      </div>
    </div>
  )
}
export interface TestFormValues {
  clientId: string
  category: string | null
  tickmillCompanyId: number | null
  version: number | null
  name: string | null
  id: string | null
  selectedAnswers: string[]
  freeAnswers: { question: string; value: string | number }[]
  tempAnswers: { question: string; value: string }[]
}

interface OuterProps {
  clientId: string
  test: TestSectionsDto
  fromExistingTest: TestAnswerDto | null
  onSubmit(values: FormSubmitValues<CreateClientTest>): void
  isCorporate?: boolean
}

export const ClientTestForm = withFormik<OuterProps, TestFormValues>({
  mapPropsToValues: (props) => {
    return initialState(props)
  },

  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      setSubmitting(true)
      props.onSubmit(dataToSubmit(values))
    } finally {
      setSubmitting(false)
    }
  },

  validate: (values, props) => {
    const errors: FormikErrors<TestFormValues> = {}
    const allQuestions = props.test.sections.flatMap((section) => section.questions)

    props.test.sections.forEach((section) => {
      section.questions
        .filter(({ widget }) =>
          showConditionalQuestions(allQuestions, values.selectedAnswers, widget)
        )
        .forEach((question) => {
          const options = question.widget.widgetOptions

          const answer =
            values.freeAnswers.find((x) => x.question === question.question.id)?.value ??
            values.selectedAnswers.find((x) => question.answers.some((a) => a.id === x)) ??
            values.tempAnswers.find((x) => x.question === question.question.id)?.value

          const isMandatory = Boolean(options?.isMandatory || question.isMandatory)

          if (isMandatory && !answer) {
            errors[question.widget.name as keyof TestFormValues] =
              question.widget.errorMessage || 'Required'
            return
          }

          if (options?.regExp) {
            const regExp = new RegExp(options.regExp)
            if (answer && !regExp.test(answer.toString())) {
              errors[question.widget.name as keyof TestFormValues] = 'Invalid format'
            }
          }
        })
    })

    return errors
  },
  enableReinitialize: true,
  validateOnMount: true,
})(ClientTestFormUI)

const handleFreeAnswers = (
  freeAnswers: { question: string; value: string | number }[],
  tempAnswers: { question: string; value: string }[]
) => {
  const values = []

  for (const freeAnswer of freeAnswers) {
    const hasFreeAnswers = tempAnswers.find(
      (tempAnswer) => tempAnswer.question === freeAnswer.question
    )

    if (!hasFreeAnswers) {
      values.push({
        testQuestionId: freeAnswer.question,
        answer: freeAnswer.value,
      })
    }
  }
  return values
}

const dataToSubmit = (values: TestFormValues): FormSubmitValues<CreateClientTest> => {
  const freeAnswers = handleFreeAnswers(values.freeAnswers, values.tempAnswers)

  return {
    testId: values.id,
    clientId: values.clientId,
    category: values.category,
    selectedAnswers: values.selectedAnswers.filter((x) => x.length > 0),
    freeAnswers: freeAnswers.concat(
      values.tempAnswers.map((x) => ({ testQuestionId: x.question, answer: x.value }))
    ),
  }
}

const isQuestionMandatory = (q: TestQuestionDto): boolean => {
  return Boolean(q.widget.widgetOptions?.isMandatory || q.isMandatory)
}

const initialState = (props: OuterProps) => {
  const { test: currentTest, fromExistingTest, clientId } = props

  const arrayAnswer: string[] = []
  const freeArrayAnswer: { question: string; value: string }[] = []

  if (!!fromExistingTest && Array.isArray(fromExistingTest.sections)) {
    fromExistingTest.sections.forEach((section) => {
      const currentTestSection = currentTest.sections.find((x) => x.id === section.id)

      if (currentTestSection) {
        section.answers.forEach((answer) => {
          const questionId = answer.question.id
          const predefinedAnswerId = answer.answer.predefinedAnswerId

          const predefinedQuestion = currentTestSection.questions.find(
            (q) => q.question.id === questionId
          )
          const predefinedAnswer = predefinedQuestion?.answers.find(
            (x) => x.answerId === predefinedAnswerId
          )

          if (predefinedQuestion && predefinedAnswer) {
            arrayAnswer.push(predefinedAnswer.id)
          } else if (predefinedQuestion && answer.answer.name) {
            freeArrayAnswer.push({ question: answer.question.id, value: answer.answer.name })
          }
        })
      }
    })
  }

  return {
    category: currentTest?.category || null,
    id: currentTest?.id,
    name: currentTest?.name,
    tickmillCompanyId: currentTest?.tickmillCompanyId,
    version: currentTest?.version,
    clientId,
    selectedAnswers: arrayAnswer,
    freeAnswers: freeArrayAnswer,
    tempAnswers: [],
  }
}

export const showConditionalQuestions = (
  questions: TestQuestionDto[],
  selectedAnswers: string[],
  widget?: TestQuestionWidget,
  path: readonly string[] = []
): boolean => {
  const parsedAnswers = Object.values(selectedAnswers ?? {}).flat()
  const dependField = widget?.widgetOptions?.depend
  const parentNames = !dependField ? [] : Array.isArray(dependField) ? dependField : [dependField]

  if (parentNames.length === 0) {
    return !widget?.widgetOptions?.hide
  }

  const currentName = widget?.name ?? ''
  if (currentName && path.includes(currentName)) return false
  const newPath = currentName ? [...path, currentName] : path

  return parentNames.some((parentName) => {
    const parentQ = questions.find((q) => q.widget.name === parentName)
    if (!parentQ) {
      return !widget?.widgetOptions?.hide
    }

    const isParentVisible = showConditionalQuestions(
      questions,
      selectedAnswers,
      parentQ.widget,
      newPath
    )
    if (!isParentVisible) return false

    const answersNeededToShow: string[] = []
    parentQ.widget.widgetOptions?.onChange
      ?.filter((change) => change.name === widget?.name)
      ?.forEach((change) => {
        if (change.values?.length) answersNeededToShow.push(...change.values)
      })

    const matched = parsedAnswers.some((a) => answersNeededToShow.includes(a))

    if (!matched) return !widget?.widgetOptions?.hide

    return true
  })
}
